/**
 * DISPATCH FORM 2.0, question for question (owner, 9 Sep 2026: "the dispatch
 * form should be identical to the Google form the company uses right now, with
 * all the selections baked in").
 *
 * Two sources, and they must agree:
 *   - the form itself (docs.google.com/forms/d/1si8STrWGXFPDb5_gRV-…), which
 *     gives the questions, their order and which are required;
 *   - its answer sheet (Form responses 1 of the dispatch workbook), which gives
 *     the exact strings the company's own tools then read.
 *
 * Where the two differ it is the SHEET that wins, because the sheet is what
 * billing, BUSY and every downstream reader match on. That is why some values
 * here carry doubled spaces ("CY  42'inch", "CLN  Li + 25 AMP") and odd
 * spellings ("ORENGE", "INVERTED", "Extra modified LOADER", "VARIENT"). They
 * are not mistakes to tidy: a corrected spelling is a new value that matches
 * nothing.
 */

import { DRIVER_OTHERS } from './drivers.js';
import type { ModelSwitches } from './catalogue.js';
import { isCityCabPro } from './pricing.js';
import { vehicleClassFor } from './sale.js';

export const DISPATCH_VEHICLE_TYPES = [
  'GARIB RATH',
  'CITYCAB EV',
  'CITYCAB CARGO',
  'CITY AUTO',
  'CITY CAB SUPER',
  'CITY CAB E-MAX',
  'CITY CAB PRO',
] as const;

/**
 * The plus variants are the E-Max's (owner, 17 Sep 2026: "e max eco + and e max
 * dlx +"). The Pro is offered its two only — see `dispatchOptionsFor`.
 */
export const DISPATCH_MODEL_TYPES = ['DLX', 'ECO', 'DLX Li', 'ECO Li', 'DLX+ Li', 'ECO+ Li', 'MINI ECO Li'] as const;

/** The CITY CAB PRO's two trims, and nothing else (owner, 24 Sep 2026). */
export const CITY_CAB_PRO_MODEL_TYPES = ['DLX Li', 'ECO Li'] as const;

/** The lead-acid trims — the sheet's plain DLX and ECO, as the L3 builds are sent. */
const LEAD_TRIMS: readonly string[] = ['DLX', 'ECO'];

export const DISPATCH_VARIENTS = [
  'PASSENGER',
  'LOADER FULL',
  'LOADER HALF 4*5',
  'Cargo Jumbo',
  'Extra modified LOADER',
  'DUST CART',
  'CARGO Without LOADER',
  'MINI CARGO',
] as const;

/** "CLN  Li + 25 AMP" carries the sheet's double space. Leave it. */
export const DISPATCH_CHARGER_MODELS = [
  'EASTMAN Li+ 25Amp',
  'EASTMAN Li+ 30Amp',
  'AXIOME Li+ 25Amp',
  'LIVGUARD Li+ 25Amp',
  'AKIRA Li+ 25 Amp',
  'INVERTED Li+ 25 Amp',
  'EASTMAN 18Amp',
  'UTL Li+ 25 Amp',
  'TRONTECK Li+ 25 amp',
  'UTL Li+ 20 Amp',
  'WITHOUT CHARGER',
  'CLN  Li + 25 AMP',
  'EASTMAN Li+ 50Amp',
] as const;

export const DISPATCH_BATTERY_COMPANIES = [
  'EASTMAN',
  'LIVGUARD',
  'AKIRA',
  'TRONTECK',
  'INVERTED',
  'UTL',
  'SYGNUS',
  'WITHOUT BATTERY',
  'CLN',
] as const;

export const DISPATCH_BATTERY_AH = [
  '120 AH + 12 MONTHS',
  '140 AH + 15 MONTHS',
  '150 AH + 18 MONTHS',
  '105AH Lithium 36M',
  '105AH Lithium 36M+ IOT',
  '132AH Lithium 36M',
  '150AH Lithium 36M',
  '105AH  61V Lithium 36M',
  '60 AH 51V',
  'HOLD',
  '140 AH Lithium 36 M',
  'WITHOUT BATTERY',
  '232 AH  Lithium 36 M',
] as const;

export const DISPATCH_COLOURS = [
  'RED',
  'BLACK',
  'WHITE',
  'YELLOW',
  'MAROON',
  'ORENGE',
  'BLUE',
  'Army GREEN',
  'Grey',
  'Brown',
  'LIGHT GREEN',
  'PINK',
  'GOLDEN',
  'SKY BLUE',
  'SHINE BLUE',
  'PARROT GREEN',
] as const;

/** The sheet's own spacing: two spaces after 999 and CY, four before 33'inch. */
export const DISPATCH_DIFFERENTIALS = [
  "999  33'inch",
  "CY    33'inch",
  "CY  42'inch",
  "999  42'inch",
] as const;

export const DISPATCH_MOTOR_COMPANIES = ['999', 'ECOTRON', 'HRS', 'TUSYO', 'Tiger'] as const;

export const DISPATCH_MOTORS = ['1000W', '1250W', '1500W', '1650W', '1800W', '2000W'] as const;

export const DISPATCH_DRIVERS = [
  'RAJU',
  'RATAN',
  'GOLU',
  'BHARAT MISHRA',
  'DEALER DRIVER',
  'customer',
  'truck',
  'Shiv Shanker Mishra',
  'VIMLESH',
  'Others',
  'SATGUR',
  'SHASHI BHUSHAN',
  'ADITYA KUMAR',
] as const;

/** One question on the form: what it is called, and what it offers. */
export interface DispatchField {
  /** The key on the dispatch payload. */
  key: string;
  /** The question, in the form's own words. */
  label: string;
  /**
   * What kind of answer it takes. A `photo` question is a picture of the part
   * — the paper form's file upload, and the reason Rahul carries a phone. A
   * `scan` question is a serial number read off the part's sticker by the
   * barcode scanner, which types it into the box (13 Oct 2026) — or typed by
   * hand where the part has no code to scan.
   */
  kind: 'select' | 'text' | 'photo' | 'scan';
  /** The dropdown, or none when the answer is typed. */
  options?: readonly string[];
  /** The form lets this one be typed when the list has no match. */
  allowOther?: boolean;
  placeholder?: string;
  /** Filled from the chassis when it comes off the store. */
  fromVehicle?: 'model' | 'variant' | 'color';
  /** The column this lands in, in the dispatch sheet. */
  extraKey?: string;
  /** The form marks it required; the app asks for it before dispatch. */
  required?: boolean;
  /** A one-line word of help under the question. */
  hint?: string;
  /**
   * Asked only of a lead-acid build (owner, 21 Oct 2026: "for lead vehicles 4
   * batteries are installed so need 4 serial number for batters") — see
   * `isLeadDispatch`. A lithium build carries one pack, and never sees it.
   */
  leadOnly?: boolean;
  /**
   * A `scan` question's other way in: a photo of the sticker, filed under this
   * kind (owner, 17 Oct 2026: "give both options image and scanner … they can
   * choose 1 whatever they want"). Either one answers the question.
   */
  photoKind?: string;
}

/**
 * The form, question by question, in its own order.
 *
 * Question 1 (the dealer) and question 8 (the chassis) are not here: the app
 * knows the buyer from the order and the chassis from the store. Question 17
 * (PASSWORD) is gone at the owner's word (10 Sep 2026: "remove password and
 * bill no line — bill is the next step, dispatch data is made for billing
 * only"); the sheet keeps its column, empty, so the layout is unchanged. There
 * has never been a bill number on this form.
 */
export const DISPATCH_FIELDS: readonly DispatchField[] = [
  { key: 'vehicleType', label: 'Type of vehicle', kind: 'select', options: DISPATCH_VEHICLE_TYPES, fromVehicle: 'model', required: true },
  { key: 'modelType', label: 'Type of model', kind: 'select', options: DISPATCH_MODEL_TYPES, fromVehicle: 'variant', required: true },
  { key: 'varient', label: 'Varient', kind: 'select', options: DISPATCH_VARIENTS, extraKey: 'flate_bed_cargo', required: true },
  { key: 'chargerModel', label: 'Charger model', kind: 'select', options: DISPATCH_CHARGER_MODELS, allowOther: true, extraKey: 'charger_model', required: true },
  { key: 'batteryCompany', label: 'Battery company', kind: 'select', options: DISPATCH_BATTERY_COMPANIES, extraKey: 'battery_company', required: true },
  { key: 'batteryAh', label: 'Battery AH', kind: 'select', options: DISPATCH_BATTERY_AH, extraKey: 'battery_ah', required: true },
  { key: 'colour', label: 'Vehicle colour', kind: 'select', options: DISPATCH_COLOURS, fromVehicle: 'color', required: true },
  { key: 'differential', label: 'Differential', kind: 'select', options: DISPATCH_DIFFERENTIALS, extraKey: 'differential', required: true },
  /*
   * The serial numbers, scanned (owner, 13 Oct 2026: "yes everything will be
   * scanner even motor controller serial number"). Each belongs to THIS vehicle
   * alone, so unlike the build above they are never remembered from the last
   * one. The photos stay as well — the owner kept them.
   *
   * Settled the next day (owner, 14 Oct 2026: "battery serial number and
   * charger serial number will be scanned and chasis number photo will be
   * uploaded like earlier", and asked: the chassis photo only). The battery
   * and the charger are scanned instead of photographed; the motor
   * controller's serial is not asked; the chassis plate is photographed as it
   * always was. The sheet keeps every column where it is — the charger and
   * battery photo columns and the controller's are written blank — and what
   * was recorded before stays on each vehicle. The server still takes a
   * controller serial from a page that sends one.
   *
   * And the photo back beside the scan (owner, 17 Oct 2026: "in dispatch form
   * give both options image and scanner people are were more use to image
   * model and now scanner also they can choose 1 whatever they want to do
   * image or scanner dosent matter"). Each of the two is answered by a scan OR
   * a photo of its sticker, the person's choice, filed under the photo kind it
   * always had — so the sheet's photo columns fill again when a photo is taken.
   */
  { key: 'batterySerialNo', label: 'Battery serial no.', kind: 'scan', extraKey: 'battery_serial_no', required: true, hint: 'Scan the battery sticker. No code on it? Type the number.', photoKind: 'dispatch_battery_serial_photo' },
  /*
   * A lead-acid build carries four 12 V batteries, each with its own serial
   * (owner, 21 Oct 2026). The first is the question above, where it always
   * was; the other three are asked only when the model type says lead, each
   * scanned or photographed like the first.
   */
  { key: 'batterySerialNo2', label: 'Battery 2 serial no.', kind: 'scan', extraKey: 'battery_serial_no_2', required: true, leadOnly: true, hint: 'The second battery’s sticker.', photoKind: 'dispatch_battery_serial_photo_2' },
  { key: 'batterySerialNo3', label: 'Battery 3 serial no.', kind: 'scan', extraKey: 'battery_serial_no_3', required: true, leadOnly: true, hint: 'The third battery’s sticker.', photoKind: 'dispatch_battery_serial_photo_3' },
  { key: 'batterySerialNo4', label: 'Battery 4 serial no.', kind: 'scan', extraKey: 'battery_serial_no_4', required: true, leadOnly: true, hint: 'The fourth battery’s sticker.', photoKind: 'dispatch_battery_serial_photo_4' },
  { key: 'chargerSerialNo', label: 'Charger serial no.', kind: 'scan', extraKey: 'charger_serial_no', required: true, hint: 'Scan the charger sticker.', photoKind: 'dispatch_charger_serial_photo' },
  { key: 'chassisPhoto', label: 'Chassis number photo', kind: 'photo', extraKey: 'chassis_number_photo', required: true, hint: 'Photograph the plate on the vehicle.' },
  { key: 'motorCompany', label: 'Motor company', kind: 'select', options: DISPATCH_MOTOR_COMPANIES, allowOther: true, extraKey: 'motor_company', required: true },
  { key: 'motor', label: 'Motor', kind: 'select', options: DISPATCH_MOTORS, allowOther: true, extraKey: 'motor', required: true },
  { key: 'driverName', label: 'Driver name', kind: 'select', options: DISPATCH_DRIVERS, allowOther: true, extraKey: 'driver_name', required: true },
];

/** What each dispatch photo is of, by its paper kind — the register's and the service desk's words. */
export const DISPATCH_PHOTO_LABELS: Record<string, string> = {
  dispatch_chassis_photo: 'Chassis number',
  dispatch_charger_serial_photo: 'Charger serial number',
  dispatch_battery_serial_photo: 'Battery serial number',
  dispatch_battery_serial_photo_2: 'Battery 2 serial number',
  dispatch_battery_serial_photo_3: 'Battery 3 serial number',
  dispatch_battery_serial_photo_4: 'Battery 4 serial number',
};

/** The photo questions, in the order the form asks them. */
export const DISPATCH_PHOTO_FIELDS = DISPATCH_FIELDS.filter((field) => field.kind === 'photo');

/** The serial numbers the scanner reads, in the order it reads them (13 Oct 2026). */
export const DISPATCH_SCAN_FIELDS = DISPATCH_FIELDS.filter((field) => field.kind === 'scan');

/** The batteries a lead-acid build carries, each with its own serial (owner, 21 Oct 2026). */
export const LEAD_BATTERY_COUNT = 4;

/** A lead-acid build: the model type is the sheet's plain trim — "DLX", "ECO" — with no "Li". */
export function isLeadDispatch(answers: { modelType?: string | null }): boolean {
  return LEAD_TRIMS.includes((answers.modelType ?? '').trim());
}

/** The serials this vehicle is asked for: one battery for a lithium build, four for a lead one. */
export function dispatchScanFieldsFor(answers: { modelType?: string | null }): readonly DispatchField[] {
  const lead = isLeadDispatch(answers);
  return DISPATCH_SCAN_FIELDS.filter((field) => !field.leadOnly || lead);
}

/** The vehicle-record keys a battery serial may sit under — all four, so a battery is found on any. */
export const BATTERY_SERIAL_EXTRA_KEYS = ['battery_serial_no', 'battery_serial_no_2', 'battery_serial_no_3', 'battery_serial_no_4'] as const;

/** Every battery serial on a vehicle's record, first to fourth, blanks left out. */
export function batterySerialsOf(extra: Record<string, unknown> | null | undefined): string[] {
  return BATTERY_SERIAL_EXTRA_KEYS.map((key) => extra?.[key]).filter((value): value is string => typeof value === 'string' && value.trim() !== '');
}

/** Shortest and longest a scanned serial may be: shorter is a misread, longer is a QR that carries more than the number. */
export const SERIAL_MIN_LENGTH = 4;
export const SERIAL_MAX_LENGTH = 120;

/**
 * A serial as it is kept: what the scanner typed, with its line ends, tabs and
 * doubled spaces taken out. Case is kept — a code may carry it. Blank → null.
 */
export function cleanSerial(text: string | null | undefined): string | null {
  const tidy = (text ?? '')
    .replace(/\p{Cc}+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return tidy === '' ? null : tidy;
}

/** What is wrong with a serial, or null when it reads like one. */
export function serialProblem(text: string | null | undefined): string | null {
  const serial = cleanSerial(text);
  if (!serial) return 'Scan it, or type the number.';
  if (serial.length < SERIAL_MIN_LENGTH) return 'Too short to be a serial — scan it again.';
  if (serial.length > SERIAL_MAX_LENGTH) return 'Too long — the code holds more than a serial. Type the number instead.';
  return null;
}

/**
 * The plate check (owner, 14 Oct 2026: "in this entry chasis number is
 * different from picture uploaded … make a rule nobody can do such mistake in
 * future"). On 13 Oct two dispatches went out on the wrong records: EM26K001183
 * was dispatched while the plate photographed was K001184, and K001184 while
 * the plate was J001051 — each time the chassis picked in the app was not the
 * one being loaded. Now the bay types the last characters it reads on the
 * plate — the series letter and six digits — and both the page and the server
 * refuse a dispatch whose plate does not end the same as the chassis picked.
 */
export const PLATE_CHECK_LENGTH = 7;

const plateKey = (text: string | null | undefined) => (text ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** What is wrong with the plate typed for the chassis picked, or null when they agree. */
export function plateCheckProblem(chassisNo: string, typed: string | null | undefined): string | null {
  const read = plateKey(typed);
  if (read.length < PLATE_CHECK_LENGTH) {
    return `Type the last ${PLATE_CHECK_LENGTH} characters on the chassis plate — the letter and six digits, e.g. K001184.`;
  }
  const picked = plateKey(chassisNo).slice(-PLATE_CHECK_LENGTH);
  const plate = read.slice(-PLATE_CHECK_LENGTH);
  if (plate !== picked) {
    return `The plate reads …${plate}, but the chassis picked is …${picked}. Pick the chassis of the vehicle in front of you.`;
  }
  return null;
}

/**
 * "DISPATCH DATA" — the raw tab, cell for cell as the Google form's own answer
 * sheet writes it (owner, 10 Sep 2026: "create a new raw tab DISPATCH DATA,
 * every dispatch filled precisely without fail").
 *
 * Captured from the live sheet on 10 Sep 2026 at 2,000 responses, trailing
 * spaces and all. `Alloy` and the two `Column` cells are the form's own
 * leftovers — columns nobody fills. They stay, empty, because the layout is
 * the contract: a reader counting columns must find the same thing here.
 */
export const DISPATCH_SHEET_TAB = 'DISPATCH DATA';

/**
 * The tab the billing desks write their bills onto (owner, 14 Sep 2026: "upload
 * it against the dispatch and all this should go to dispatch sheet create a
 * column in the sheet as well for bill no").
 *
 * A tab of its own because the app can only ADD rows to the owner's sheet — the
 * webhook appends and nothing in it can go back and change a row already sent.
 * A bill is raised days after the dispatch, so it can never be written onto the
 * dispatch's own line directly. One formula in DISPATCH DATA looks it up here by
 * chassis instead, which covers every past row and every future one.
 */
export const BILLS_SHEET_TAB = 'BILLS';

/** What that tab holds. The chassis is the join, so it comes first. */
export const BILLS_SHEET_HEADER = [
  'CHASSIS NUMBER',
  'BILL NO',
  'BILL DATE',
  'BILL FILE',
  'BILLED TO',
  'BILLED BY',
] as const;

export const DISPATCH_SHEET_HEADER = [
  'Timestamp',
  'Dealer Name',
  'TYPE OF VEHICLE',
  'TYPE OF MODEL',
  'VARIENT ',
  'CHARGER MODEL',
  'BATTERY COMPANY',
  'BATTERY AH',
  'Alloy',
  'CHASSIS NUMBER',
  'VEHICLE COLOUR',
  'DIFFERENTIAL',
  'CHASSIS NUMBER PHOTO',
  'CHARGER SERIAL NUMBER PHOTO ',
  'BATTERY SERIAL NUMBER',
  'MOTOR COMPANY',
  'MOTOR ',
  'DRIVER NAME',
  'PASSWORD',
  'Column 1',
  'Column 2',
  // V and W: the BILL NO and BILL FILE lookups the sheet keeps beside the row
  // (14 Sep 2026). Untitled here so the app never writes over them.
  '',
  '',
  // The scanned serials (13 Oct 2026), after the bill columns. They land by
  // NAME once these headings stand in X1, Y1 and Z1 of the tab.
  'BATTERY SERIAL NO',
  'CHARGER SERIAL NO',
  'MOTOR CONTROLLER SERIAL NO',
] as const;

/** Which answer fills which column of that tab. A blank means the column stays empty. */
export const DISPATCH_SHEET_COLUMNS: ReadonlyArray<string | null> = [
  'timestamp',
  'dealerName',
  'vehicleType',
  'modelType',
  'varient',
  'chargerModel',
  'batteryCompany',
  'batteryAh',
  null, // Alloy — the form stopped asking; the column stays.
  'chassisNo',
  'colour',
  'differential',
  'chassisPhoto',
  'chargerSerialPhoto',
  'batterySerialPhoto',
  'motorCompany',
  'motor',
  'driverName',
  null, // PASSWORD — removed from the form, 10 Sep 2026.
  null,
  null,
  null, // BILL NO lookup — the sheet's own.
  null, // BILL FILE lookup — the sheet's own.
  'batterySerialNo',
  'chargerSerialNo',
  'controllerSerialNo',
];

/**
 * The choices a field offers, given the answers so far. An L5 body — the
 * E-Max and the Pro — is built with lithium only (owner, 17 Sep 2026: "L5
 * only comes in lithium"), so once the vehicle type says L5 the model list
 * drops the lead variants rather than let a wrong one be picked. The Pro
 * narrows further, to its two trims (owner, 24 Sep 2026: "There is no deluxe
 * plus and eco plus in city cab pro only city cab pro eco li and city cab pro
 * dlx li").
 *
 * `leadByException` is an L5 sent on an order that asked for lead acid by the
 * office's exception (owner, 18 Oct 2026: "Can you make and exception i need to
 * send only one lead L5"): its lithium trims stay, and the lead trims — the
 * sheet's plain DLX and ECO — are offered beside them, so the vehicle is
 * recorded as what it is rather than as a lithium build.
 */
export function dispatchOptionsFor(
  field: DispatchField,
  answers: Record<string, string | undefined>,
  /**
   * What the office has switched off (owner, 24 Sep 2026: "Add a toggle for
   * switching vehicle model off and on"). Optional, so a caller without the
   * switches gets the full lists as before. A value the form already holds is
   * the caller's to keep offering ("as recorded") — this only says what is on.
   */
  switches?: ModelSwitches | null,
  exception: { leadByException?: boolean } = {},
): readonly string[] {
  const options = field.options ?? [];
  // The drivers list the office keeps (5 Nov 2026), then the choices that are no person on it.
  if (field.key === 'driverName' && switches?.drivers?.length) return [...switches.drivers, ...DRIVER_OTHERS];
  if (field.key === 'vehicleType') {
    const off = switches?.vehicleTypesOff ?? [];
    return off.length ? options.filter((option) => !off.includes(option)) : options;
  }
  if (field.key !== 'modelType') return options;
  const model = answers.vehicleType ?? '';
  // Own keys only: a vehicle type spelt like an Object method must not reach the prototype.
  const trimsOff = model && switches?.trimsOff && Object.hasOwn(switches.trimsOff, model) ? (switches.trimsOff[model] ?? []) : [];
  const on = (list: readonly string[]) => (trimsOff.length ? list.filter((option) => !trimsOff.includes(option)) : list);
  if (!model || vehicleClassFor(model, null) !== 'L5') return on(options);
  const lead = exception.leadByException ? options.filter((option) => LEAD_TRIMS.includes(option)) : [];
  if (isCityCabPro(model)) return on([...options.filter((option) => (CITY_CAB_PRO_MODEL_TYPES as readonly string[]).includes(option)), ...lead]);
  return on([...options.filter((option) => /\bLi\b/i.test(option)), ...lead]);
}

/**
 * Every trim a vehicle type can offer at all, before any switch — the list the
 * Models section shows its switches against.
 */
export function trimsForVehicleType(vehicleType: string): readonly string[] {
  const modelType = DISPATCH_FIELDS.find((field) => field.key === 'modelType');
  return modelType ? dispatchOptionsFor(modelType, { vehicleType }) : [];
}
